/**
 * Size descriptions (snippets/variant-main-picker.liquid) sit in their own container right after the option group
 * (.variant-option__group = the buttons' fieldset + .variant-option__size-tips). For each container this sets:
 * - --size-tip-space: the tallest description's height (max 2 lines), so switching sizes never moves Add to Cart;
 * - --size-tip-left per description: left-aligned under the leftmost button of a row, right-aligned under the
 *   rightmost one, otherwise centred under its button and kept inside the group;
 * - --arrow-left per description: the up-arrow under its button's centre (any row).
 *
 * Re-runs whenever a group changes size (wrapping rows, resize, the quick-add panel opening), after fonts load,
 * and when pickers appear or are re-rendered (variant changes, quick add).
 */
const GROUP = '.variant-option__group';
const TIPS = '.variant-option__size-tips';
/** Keep the arrow this far from the box's rounded corners */
const ARROW_INSET = 12;

/** @param {Element} group */
function layoutGroup(group) {
  const container = group.querySelector(`:scope > ${TIPS}`);
  const fieldset = group.querySelector(':scope > fieldset');
  if (!(container instanceof HTMLElement) || !fieldset) return;
  const tips = /** @type {HTMLElement[]} */ ([...container.children]);
  const box = container.getBoundingClientRect();
  if (!tips.length || !box.width) return;

  container.style.setProperty('--size-tip-space', `${Math.max(...tips.map((tip) => tip.offsetHeight))}px`);

  // Buttons by index, and the left / right edge of each row (rows follow however the buttons wrap)
  const buttons = new Map();
  /** @type {{ top: number, left: number, right: number }[]} */
  const rows = [];
  fieldset.querySelectorAll('.variant-option__button-label').forEach((label) => {
    const index = label.querySelector('input')?.dataset.inputIndex;
    const rect = label.getBoundingClientRect();
    if (index === undefined || !rect.width) return;
    buttons.set(index, rect);
    const row = rows.find((r) => Math.abs(r.top - rect.top) < 4);
    if (row) {
      row.left = Math.min(row.left, rect.left);
      row.right = Math.max(row.right, rect.right);
    } else {
      rows.push({ top: rect.top, left: rect.left, right: rect.right });
    }
  });

  const originX = box.left + container.clientLeft;
  const room = container.clientWidth;
  for (const tip of tips) {
    const rect = buttons.get(tip.dataset.index);
    if (!rect) continue;
    const row = rows.find((r) => Math.abs(r.top - rect.top) < 4);
    const width = tip.offsetWidth;
    const centre = rect.left + rect.width / 2 - originX;
    let left = centre - width / 2;
    if (row && Math.abs(rect.left - row.left) < 1) left = rect.left - originX; // leftmost: box starts at its left edge
    else if (row && Math.abs(rect.right - row.right) < 1) left = rect.right - originX - width; // rightmost: ends at its right edge
    left = Math.max(0, Math.min(left, room - width));
    const arrow = Math.max(ARROW_INSET, Math.min(centre - left, width - ARROW_INSET));
    tip.style.setProperty('--size-tip-left', `${Math.round(left)}px`);
    tip.style.setProperty('--arrow-left', `${Math.round(arrow)}px`);
  }
}

// Laid out in the next frame: setting --size-tip-space changes the container's height
const resizeObserver = new ResizeObserver((entries) => {
  requestAnimationFrame(() => {
    for (const entry of entries) layoutGroup(entry.target);
  });
});
/** @type {WeakSet<Element>} */
const observed = new WeakSet();

function watchGroups() {
  document.querySelectorAll(GROUP).forEach((group) => {
    if (!group.querySelector(`:scope > ${TIPS}`)) return;
    if (!observed.has(group)) {
      observed.add(group);
      resizeObserver.observe(group); // also lays it out once when observation starts
    } else {
      layoutGroup(group);
    }
  });
}

let frame = 0;
function schedule() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(watchGroups);
}

schedule();
document.fonts?.ready.then(schedule);
document.addEventListener('variant:update', () => setTimeout(schedule, 0));

// Pickers that appear or get replaced later (quick-add panel, section re-renders).
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element && (node.matches(TIPS) || node.querySelector(TIPS))) {
        schedule();
        return;
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
